import { CircleCheck, NotepadText } from "lucide-react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import ColumnCard, { ColumnProp } from "@/components/ColumnCard";
import { JobSearchSolid } from "@/components/icons";
import { format } from "date-fns";

import SlideOver from "@/components/SlideOver"
import IconButton from "@/components/buttons/IconButton"
import Box from "@/components/Box";
import Badge from "@/components/Badge";
import { useGetInspectionDetails } from "@/features/Inspections/repository";


export default function InspectionsDetail () {
    const navigate = useNavigate();
    const location = useLocation();
    const { id } = useParams();

    // The row that was clicked hands the inspection over in router state, which is
    // the fast path. It is absent on a refresh or a shared link, and the panel then
    // rendered nothing at all — so fall back to fetching the inspection by its id.
    const fromRow = location.state?.inspection;
    const { isLoading, inspection: fetched } = useGetInspectionDetails(fromRow ? "" : (id ?? ""));
    const inspection = fromRow ?? fetched?.results;

    const close = () => navigate(-1);
    const getStatusBadgeIntent = (status: string | null) => {
        switch (status) {
            case "PENDING":
                return "warning";
            case "IN_PROGRESS":
                return "primary";
            case "COMPLETED":
                return "success";
            case "CANCELLED":
                return "warning";
            default:
                return "info";
        }
    };
    if (!inspection) {
        return (
            <SlideOver
                title="Inspection Preview"
                size="md"
                onClose={close}
                isOpen={true}
            >
                <p className="text-sm text-gray-600">
                    {isLoading
                        ? "Loading inspection details…"
                        : "This inspection could not be found."}
                </p>
            </SlideOver>
        );
    }

    const columns: ColumnProp[] = [
        {
            heading: "Location",
            content: inspection.location || "Not specified"
        },
        {
            heading: "Schedule Date",
            content: inspection.scheduledDate ?
                format(new Date(inspection.scheduledDate), 'dd MMM, yyyy') :
                "Not scheduled"
        },
        {
            heading: "Inspector",
            content: inspection.inspectorNames?.[0] || "Not assigned"
        },
    ];

    // Headed "Customer Details" while holding no customer data — the API's
    // inspection carries no applicant name, only the references below.
    const applicationInfos = [
        { 
            label: "Application ID", 
            value: inspection.applicationId || "N/A" 
        },
        { 
            label: "Inspection ID", 
            value: inspection.id ? `INSP-${inspection.id}` : "N/A" 
        },
        {   
            label: "Schedule Date", 
            value: inspection.scheduledDate ?
                format(new Date(inspection.scheduledDate), 'dd MMM, yyyy') : "Not Scheduled" 
        },
    ];

    return (
        <SlideOver
            title={`${inspection.type || 'Regular'} Inspection Preview`}
            icon={(
                <IconButton
                    intent="primary"
                    className="border border-zinc-200 shadow-sm p-2.5 rounded-lg"
                >
                    <JobSearchSolid className="size-6" />
                </IconButton>
            )}
            size="md"
            onClose={close}
            isOpen={true}
        >
            <div className="space-y-4 md:space-y-8">
                <Box>
                    <div className="flex justify-between items-start gap-4 mb-4">
                        <div className="space-y-1">
                            <h5 className="text-secondary font-semibold">
                                #{inspection.id}
                            </h5>
                            <p className="text-sm font-light">
                                {inspection.description || "No description available"}
                            </p>
                        </div>
                        <Badge
                            intent={getStatusBadgeIntent(inspection.status)}
                            text={inspection.status || "PENDING"}
                        />
                    </div>
                    <ColumnCard columns={columns} className="grid-cols-3" />
                </Box>

                <section>
                    <h4 className="text-secondary xl:text-lg font-medium mb-4">
                        Application Details
                    </h4>
                    <ul className="space-y-2 font-light text-sm text-secondary">
                        {
                            applicationInfos.map((el, index) => (
                                <li className="grid grid-cols-2 gap-2" key={index}>
                                    <div>{el.label}</div>
                                    <div className="font-semibold">
                                        {el.value}
                                    </div>
                                </li>
                            ))
                        }
                    </ul>
                </section>

                <section>
                    <h4 className="text-secondary xl:text-lg font-medium mb-4">
                        Areas of Inspection
                    </h4>
                    <div className="space-y-2">
                        {
                            inspection.areasOfInspection?.map((area:any, index:any) => (
                            <Box className="flex justify-between items-center gap-2" key={index}>
                                <h4 className="text-sm text-secondary">
                                    {area.name}
                                </h4>
                                <div className="text-sm">
                                    {area.passed ? (
                                        <div className="flex items-center text-green-800 gap-1">
                                            <CircleCheck className="size-5" fill="currentColor" stroke="white" />
                                            <span>Passed</span>
                                        </div>
                                    ) : (
                                        <span className="text-[#6B5C4F]">Pending</span>
                                    )}
                                </div>
                            </Box>
                        ))}
                    </div>
                </section>
                
                <section>
                    <h4 className="text-secondary xl:text-lg font-medium mb-4">
                        Description
                    </h4>
                    <Box className="border-secondary-100 text-sm p-0">
                        <div className="flex justify-between items-center gap-4 border-b border-secondary-100 py-2 px-4">
                            <div className="flex items-center gap-3">
                                <div className="bg-zinc-100 text-primary rounded-full max-w-max p-2">
                                    <NotepadText className="size-5" />
                                </div>
                                <div className="flex gap-1">
                                    <span className="text-[#6B5C4F]">Created by</span>
                                    <span className="text-secondary font-semibold">
                                        {inspection.createdBy || "Unknown"}
                                    </span>
                                </div>
                            </div>
                            <div className="flex gap-1">
                                <span className="text-[#6B5C4F]">Created on</span>
                                <span className="text-secondary font-semibold">
                                    {inspection.createdAt ?
                                        format(new Date(inspection.createdAt), 'dd MMM, yyyy') :
                                        "Unknown date"
                                    }
                                </span>
                            </div>
                        </div>
                        <div className="text-[#6B5C4F] p-4">
                            <p>{inspection.description || "No description available"}</p>
                        </div>
                    </Box>
                </section>
            </div>
        </SlideOver>
    );
};
